import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { from, Subject, takeUntil } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { LanguageService } from 'src/app/services/language.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { parseJwt } from 'src/app/utilities/fn';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  @Input() cats: any[] = [];
  @Output() menuClick = new EventEmitter<void>();

  userName = '';
  isDarkMode = false;
  isModuleDropdownOpen = false;
  isUserMenuOpen = false;
  isLanguageDropdownOpen = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private authService: AuthService,
    private spinner: NgxSpinnerService,
    public translate: TranslateService,
    private languageService: LanguageService,
    private readonly oauthService: OAuthService,
    private apiService: ApiService,
  ) {}

  get userEmail(): string {
    const t = parseJwt(this.oauthService.getAccessToken());
    return (t.email as string) || (t.preferred_username as string) || '';
  }

  get avatarStyle(): Record<string, string> {
    const u = this.authService.getUserImage();
    const url = u || 'assets/images/walter.png';
    return { 'background-image': `url("${url}")` };
  }

  ngOnInit(): void {
    this.userName = this.authService.getUserName();
    const savedTheme = localStorage.getItem('theme');
    this.isDarkMode = savedTheme === 'dark';
    this.applyTheme();

    from(this.languageService.getLanguagePromise()).subscribe(() => {
      const language = this.languageService.getLanguage();
      this.translate.addLangs(['vi', 'en', 'cn', 'jn']);
      if (language) {
        this.translate.use(language);
      }
    });
  }

  toggleTheme(): void {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem('theme', this.isDarkMode ? 'dark' : 'light');
    this.applyTheme();
  }

  applyTheme(): void {
    const el = document.documentElement;
    if (this.isDarkMode) {
      el.classList.add('dark');
    } else {
      el.classList.remove('dark');
    }
  }

  saveLang(lang: string): void {
    this.isLanguageDropdownOpen = false;
    this.apiService
      .setUserLanguage({ acceptLanguage: lang })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (results: any) => {
          if (results.status === 'success') {
            this.languageService.setLanguage(lang);
            this.translate.use(lang);
            localStorage.setItem('currentLang', lang);
            window.location.reload();
          } else {
            this.spinner.hide();
          }
        },
        () => {
          this.spinner.hide();
        },
      );
  }

  goDeviceActivity(): void {
    this.isUserMenuOpen = false;
    this.router.navigate(['/device-activity']);
  }

  logout(): void {
    this.isUserMenuOpen = false;
    this.oauthService.revokeTokenAndLogout(
      {
        client_id: this.oauthService.clientId,
        returnTo: this.oauthService.redirectUri,
      },
      true,
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
